import { Button, ButtonProps } from '@mui/material';
import { forwardRef, useEffect, useRef } from 'react';

export type AgLoadingButtonProps = {
  preventMultipleClicks?: boolean;
} & ButtonProps;

/**
 * A wrapper for the MUI Lab LoadingButton that provides standard behaviour
 * across our apps.
 *
 * One specific behaviour is to ensure onClick() is only called once per "load".
 * That is, it prevents it being called multiple times before the loading state
 * has been updated to prevent clicks (due to the render not having finished)
 * which was an issue encountered in [EVNT-229]. This can be disabled by setting
 * preventMultipleClicks to false.
 *
 * If we want to change default behaviour (spinner etc) in the future this will
 * be the place to do it.
 */
const AgLoadingButton = forwardRef<HTMLButtonElement, AgLoadingButtonProps>(
  function AgLoadingButton(
    { preventMultipleClicks = true, onClick, loading, ...otherProps }: AgLoadingButtonProps,
    ref
  ) {
    const alreadyClicked = useRef(false);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      // In some situations we found the loading state is not updated before a
      // second click event is fired (i.e two clicks in one render). Using a ref
      // avoids this.
      if (preventMultipleClicks && alreadyClicked.current) return;
      alreadyClicked.current = true;

      if (onClick) onClick(event);
    };

    // When the loading state is changed, we need to update the alreadyClick ref,
    // otherwise onClick() will never get called again, even once the job is
    // complete.
    useEffect(() => {
      if (preventMultipleClicks && !loading) {
        alreadyClicked.current = false;
      }
    }, [loading, preventMultipleClicks]);

    return <Button onClick={handleClick} loading={loading} ref={ref} {...otherProps} />;
  }
);

export default AgLoadingButton;
